import { useMemo, useState } from 'react'

import { toast } from '@webapp/components/Notification'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardAccount } from '@webapp/modules/accounts-payable/entities/CreditCardAccount'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import messages from './messages'
import {
  isMoveRelatedItemsError,
  RELATED_ITEMS_FOUND,
  useMutateMoveCreditCardItems,
} from '../../models'

// Payload carried by the RELATED_ITEMS_FOUND rejection — the FS renders it
// through MoveItemsWarningContent and confirms with allItemIds (BHV-023).
export interface RelatedItemsPayload {
  affectedItems: { item: CreditCardItem; relatedCount: number }[]
  allItemIds: number[]
}

export class RelatedItemsRejection extends Error {
  readonly type = RELATED_ITEMS_FOUND

  constructor(readonly payload: RelatedItemsPayload) {
    super(RELATED_ITEMS_FOUND)
  }
}

export const isRelatedItemsRejection = (
  error: unknown
): error is RelatedItemsRejection => error instanceof RelatedItemsRejection

export interface IUseMoveCreditCardItemsPresenterParams {
  /** Selection lives in the FS — bridged from list-credit-card-items (ADR-9) */
  selectedItems: CreditCardItem[]
  /**
   * Excludes the current card from the picker; currency for the total.
   * Nullable because the FS companion hook calls this before the account
   * query resolves — move mode is unreachable until it has.
   */
  sourceAccount: Nullable<CreditCardAccount>
  onDeselectAll: () => void
  /** FS clears selection + exits move mode; the list refetches (BHV-024) */
  onMoved: () => void
}

// FS-owned-modal command pattern (client-side precheck variant): companion
// hook only — no self-rendering Presenter component. The FS renders the
// barrel-exported MoveItemsActionBar and owns the warning ModalDialog.
export const useMoveCreditCardItemsPresenter = ({
  selectedItems,
  sourceAccount,
  onDeselectAll,
  onMoved,
}: IUseMoveCreditCardItemsPresenterParams) => {
  const intl = useProcurifyIntl()
  const { moveCreditCardItems, isMoving } = useMutateMoveCreditCardItems()

  const [targetCard, setTargetCard] =
    useState<Nullable<CreditCardAccount>>(null)

  const selectionTotal = useMemo(() => {
    const total = selectedItems.reduce((sum, item) => sum + item.totalCost, 0)

    return `${intl.formatNumber(total, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${sourceAccount?.currency.name ?? ''}`.trim()
  }, [intl, selectedItems, sourceAccount])

  const showNoTargetToast = () => {
    // Info toast — exact Angular parity strings (BHV-024).
    toast.info({
      title: intl.formatMessage(messages.noTargetTitle),
      children: intl.formatMessage(messages.noTargetMessage),
    })
  }

  const showSuccessToast = (count: number, name: string) => {
    toast.success({
      title: intl.formatMessage(messages.successTitle),
      children: intl.formatMessage(messages.successMessage, { count, name }),
    })
  }

  const showFailureToast = () => {
    toast.error({
      title: intl.formatMessage(messages.errorTitle),
      children: intl.formatMessage(messages.errorMessage),
    })
  }

  // Phase 1 — precheck + move. Rejects with RELATED_ITEMS_FOUND (payload for
  // the FS warning modal) when any selected item has related items.
  const move = async (items: CreditCardItem[]) => {
    if (!targetCard) {
      showNoTargetToast()
      return
    }

    const itemIds = items.map((item) => item.id)

    try {
      await moveCreditCardItems({
        targetCreditCardId: targetCard.id,
        itemIds,
      })

      showSuccessToast(itemIds.length, targetCard.name)
      onMoved()
    } catch (error) {
      if (isMoveRelatedItemsError(error)) {
        const affectedItems = error.results
          .filter((result) => result.relatedItemIds.length > 0)
          .reduce<RelatedItemsPayload['affectedItems']>((rows, result) => {
            const item = items.find(
              (selectedItem) => selectedItem.id === result.id
            )

            return item
              ? [...rows, { item, relatedCount: result.relatedItemIds.length }]
              : rows
          }, [])

        const allItemIds = Array.from(
          new Set<number>(
            error.results.reduce<number[]>(
              (ids, result) => [
                ...ids,
                ...result.selectedItemIds,
                ...result.relatedItemIds,
              ],
              []
            )
          )
        )

        throw new RelatedItemsRejection({ affectedItems, allItemIds })
      }

      showFailureToast()
    }
  }

  // Phase 2 — FS confirm path; related ids are folded into allItemIds.
  const moveConfirmed = async (allItemIds: number[]) => {
    if (!targetCard) {
      showNoTargetToast()
      return
    }

    try {
      await moveCreditCardItems({
        targetCreditCardId: targetCard.id,
        itemIds: allItemIds,
        skipRelatedItemsCheck: true,
      })

      showSuccessToast(allItemIds.length, targetCard.name)
      onMoved()
    } catch {
      showFailureToast()
    }
  }

  return {
    commands: {
      move,
      moveConfirmed,
    },
    states: {
      isMoving,
    },
    targetCard,
    onTargetCardChange: setTargetCard,
    selectionTotal,
    onDeselectAll,
  }
}
